import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Paper,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  Church as ChurchIcon,
  GearSix as AdminIcon,
  PaperPlaneTilt as ResourcesIcon,
  UserCircle as AccountIcon,
  UserList as AttendeesIcon,
} from "@phosphor-icons/react";

import { isAdmin, isChurchManager, isManagerRole } from "../utils/helper";
import { useUserData } from "./DataProvider";

const useStyle = makeStyles((theme) => ({
  bottomNavBar: {
    flex: 1,
    width: "100%",
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
  },
}));

const publicTabs = [
  {
    label: "Churches",
    value: "churches",
    icon: <ChurchIcon weight={"fill"} size={24} />,
  },
];

const adminTabs = [
  {
    label: "Admin",
    value: "admin",
    icon: <AdminIcon weight={"fill"} size={24} />,
  },
];

function BottomNavBar() {
  const { authStatus } = useAuthenticator((context) => [context.user]);
  const { attributes } = useUserData();
  const [tabs, setTabs] = useState(publicTabs);
  const classes = useStyle();
  const navigate = useNavigate();
  const [selected, setSelected] = useState("");
  const location = useLocation();

  useEffect(() => {
    // Update the selected tab based on the current route
    const path = location.pathname;
    const pathSegments = path.split("/");

    switch (path) {
      case "/":
        setSelected("/");
        break;
      case "/admin":
        setSelected("admin");
        break;
      case "/admin/create_admin":
        setSelected("admin");
        break;
      case "/admin/delete_user":
        setSelected("admin");
        break;
      case "/admin/invite_church":
        setSelected("admin");
        break;
      case "/admin/dashboard":
        setSelected("admin");
        break;
      case "/admin/invitations":
        setSelected("admin");
        break;
      case "/admin/users":
        setSelected("admin");
        break;
      case "/admin/overview":
        setSelected("admin");
        break;
      case "/churches":
        setSelected("churches");
        break;
      case "/account":
        setSelected("account");
        break;
      case "/account/login":
        setSelected("account/login");
        break;
      case "/attendees":
        setSelected("attendees");
        break;
      case `/attendees/${pathSegments[2]}`:
        isChurchManager(attributes, pathSegments[2])
          ? setSelected("attendees")
          : setSelected("churches");
        break;
      case "/resources":
        setSelected("resources");
        break;
      default:
        setSelected("churches");
    }
  }, [location, attributes]);
  const signInUrl = "account/login";

  useEffect(() => {
    if (authStatus !== "authenticated") {
      //User is logged out
      const signInTab = {
        label: "Sign In",
        value: signInUrl,
        icon: <AccountIcon weight={"fill"} size={24} />,
      };
      setTabs([...publicTabs, signInTab]);
    } else {
      const accountTab = {
        label: "Account",
        value: "account",
        icon: <AccountIcon weight={"fill"} size={24} />,
      };
      const attendeesTab = {
        label: "Attendees",
        value: "attendees",
        icon: <AttendeesIcon weight={"fill"} size={24} />,
      };
      const resourcesTab = {
        label: "Resources",
        value: "resources",
        icon: <ResourcesIcon weight={"fill"} size={24} />,
      };
      if (isAdmin(attributes)) {
        //User is logged in as admin
        setTabs([...adminTabs, resourcesTab, ...publicTabs, accountTab]);
      } else if (isManagerRole(attributes)) {
        setTabs([resourcesTab, attendeesTab, ...publicTabs, accountTab]);
      } else {
        //User is logged in and not admin nor church manager
        setTabs([...publicTabs, accountTab]);
      }
    }
  }, [authStatus, attributes]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelected(newValue);
    navigate(newValue);
    if (newValue === signInUrl) {
      navigate(0); // Force reload to ensure correct UI
    }
  };

  return (
    <Box>
      <Paper
        className={classes.bottomNavBar}
        elevation={3}
        sx={{
          zIndex: 20,
        }}
      >
        <BottomNavigation
          value={selected}
          onChange={handleTabChange}
          showLabels
        >
          {tabs.map((item, index) => (
            <BottomNavigationAction
              disableTouchRipple
              key={index}
              {...item}
              sx={{
                "&.Mui-selected": {
                  fontSize: "1.5rem",
                  color: "#00169E", // Change text color when selected
                },
              }}
            />
          ))}
        </BottomNavigation>
      </Paper>
    </Box>
  );
}

export default BottomNavBar;
